import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  FetchableResourceType,
  PlanningLevelConfig,
  ResourceType,
} from 'api/resource-management/types';
import { EventDragParams } from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { bulkActionsMapActions } from 'components/organisms/resource-management/utils/bulk-actions';
import {
  endDateParser,
  getDateAfter,
  isDateBetweenRange,
  shiftDateIfOnNonWorkingDay,
  startDateParser,
} from 'components/organisms/resource-management/utils/dates';
import {
  asAllocationPayload,
  idForNewAllocation,
  isOverlapping,
  newAllocationEvent,
  NewAllocationParam,
} from 'components/organisms/resource-management/utils/events-conversion/common';
import { filterAllocationEvents } from 'components/organisms/resource-management/utils/events/events';
import {
  getMaximumEndDateFromListOfRanges,
  getMinimumStartDateFromListOfRanges,
} from 'components/organisms/resource-management/utils/resources-summary/common';
import {
  isNonWorkingDay,
  numberOfWorkingDaysBetweenDates,
} from 'components/organisms/timesheets/common/utils';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { ExtendedManagerProps } from '../../types';
import { useTotalAllocationInUIManager } from '../../ui-total-allocation-manager';

type ReturnProps = (params: EventDragParams) => void;

export const useAllocationDragHandler = (
  props: ExtendedManagerProps,
): ReturnProps => {
  const {
    schedulerRef,
    groupBy,
    workingDaysConfig,
    bulkUpdate,
    excludeNonWorkingDays,
    resourceManagementConfig,
  } = props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { updateTotalAllocation } = useTotalAllocationInUIManager(props);

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  return useCallback(
    ({ event: allocationEvent, from, to }: EventDragParams) => {
      schedulerRef.current.instance.suspendRefresh();
      const fromStartDate = moment(from.startDate);
      const fromEndDate = moment(from.endDate);
      let toStartDate = moment(to.startDate);
      let toEndDate = moment(to.endDate);
      let differenceInTotalMinutes = 0;
      const originalNumberOfDays = numberOfWorkingDaysBetweenDates(
        fromStartDate,
        fromEndDate,
        workingDaysConfig,
      );

      if (isNonWorkingDay(toStartDate, workingDaysConfig)) {
        toStartDate = shiftDateIfOnNonWorkingDay(
          toStartDate,
          workingDaysConfig,
        );
        toEndDate = toStartDate
          .add(originalNumberOfDays - 1, 'day')
          .endOf('day');
      }
      toEndDate = getDateAfter(
        toStartDate,
        workingDaysConfig,
        originalNumberOfDays,
      );

      const {
        updateAllocation,
        deleteAllocation,
        createAllocation,
        actionsMap,
      } = bulkActionsMapActions();

      const oldTotalMinutes = allocationEvent.get('totalMinutes');

      // If this diff is 1, the allocation drag is finished EXACTLY snapped to a week
      const diffBetweenWeekStart = toStartDate.diff(
        toStartDate.startOf('week'),
        'day',
      );

      // If the allocation drag is not snapped to a week, snapping logic
      if (isHoursPerWeek && diffBetweenWeekStart !== 1) {
        // If diff is below 4, it is closer to the previous week
        if (
          (toStartDate.isBefore(fromStartDate, 'day') &&
            diffBetweenWeekStart < 4) ||
          (toStartDate.isAfter(fromStartDate, 'day') &&
            diffBetweenWeekStart <= 2)
        ) {
          toStartDate = toStartDate.startOf('week').add(1, 'day');
          toEndDate = toEndDate
            .subtract(1, 'week')
            .endOf('week')
            .subtract(1, 'day');
          // If diff is 4 or above, it is closer to the next week
        } else {
          toStartDate = toStartDate
            .add(1, 'week')
            .startOf('week')
            .add(1, 'day');
          toEndDate = toEndDate.endOf('week').subtract(1, 'day');
        }
      }

      allocationEvent.setStartEndDate(
        toStartDate.toDate(),
        toEndDate.toDate(),
        false,
      );

      const totalMinutes =
        numberOfWorkingDaysBetweenDates(
          toStartDate,
          toEndDate,
          workingDaysConfig,
        ) * allocationEvent.get('minutes');

      allocationEvent.set('totalMinutes', totalMinutes);
      differenceInTotalMinutes =
        differenceInTotalMinutes - oldTotalMinutes + totalMinutes;

      if (isHoursPerWeek) {
        const newStart = moment(allocationEvent.startDate);
        const newEnd = moment(allocationEvent.endDate);

        updateAllocation({
          ...asAllocationPayload(allocationEvent),
          startDate: shiftDateIfOnNonWorkingDay(
            newStart,
            workingDaysConfig,
          ).format(DEFAULT_DATE_FORMAT),
          endDate: shiftDateIfOnNonWorkingDay(newEnd, workingDaysConfig).format(
            DEFAULT_DATE_FORMAT,
          ),
        });
      } else {
        updateAllocation({
          ...asAllocationPayload(allocationEvent),
          startDate: toStartDate.format(DEFAULT_DATE_FORMAT),
          endDate: toEndDate.format(DEFAULT_DATE_FORMAT),
        });
      }

      const isMovedWithEventSpan = isOverlapping(
        {
          startDate: fromStartDate,
          endDate: fromEndDate,
        },
        { startDate: toStartDate, endDate: toEndDate },
      );
      const fromRange = {
        fromDate: fromStartDate,
        toDate: fromEndDate,
      };

      const toRange = isMovedWithEventSpan
        ? {
            fromDate: fromStartDate.isBefore(toStartDate, 'day')
              ? fromStartDate
              : toStartDate,
            toDate: fromEndDate.isAfter(toEndDate, 'day')
              ? fromEndDate
              : toEndDate,
          }
        : {
            fromDate: toStartDate,
            toDate: toEndDate,
          };

      const updatedFromRange = { ...fromRange };
      const updatedToRange = { ...toRange };

      const allEvents: AllocationEventModel[] =
        schedulerRef.current.instance.eventStore.allRecords;

      const userId = allocationEvent.get('userId');

      const eventsInSameTimeline = filterAllocationEvents(allEvents, {
        fromDate: toRange.fromDate,
        toDate: toRange.toDate,
        resourceIdentifier: {
          ...(userId != null
            ? {
                userId,
                type: ResourceType.User,
              }
            : {
                roleId: allocationEvent.get('roleId'),
                type: ResourceType.Role,
                teamId: allocationEvent.get('teamId'),
              }),
        },
      });

      eventsInSameTimeline.forEach((event) => {
        if (
          event.resourceId !== allocationEvent.resourceId ||
          event.id === allocationEvent.id
        ) {
          return;
        }

        const startDateIsBetween = isDateBetweenRange(
          startDateParser(event.startDate),
          {
            startDate: startDateParser(allocationEvent.startDate),
            endDate: endDateParser(allocationEvent.endDate),
          },
        );

        const endDateIsBetween = isDateBetweenRange(
          startDateParser(event.endDate),
          {
            startDate: startDateParser(allocationEvent.startDate),
            endDate: endDateParser(allocationEvent.endDate),
          },
        );

        const isDraggedEventInBetween =
          isDateBetweenRange(startDateParser(allocationEvent.startDate), {
            startDate: startDateParser(event.startDate),
            endDate: endDateParser(event.endDate),
          }) &&
          isDateBetweenRange(startDateParser(allocationEvent.endDate), {
            startDate: startDateParser(event.startDate),
            endDate: endDateParser(event.endDate),
          });

        if (startDateIsBetween && endDateIsBetween) {
          deleteAllocation(asAllocationPayload(event));
          event.set('minutes', 0);
          differenceInTotalMinutes -= event.get('totalMinutes');
          schedulerRef.current.instance.eventStore.remove(event);
        } else if (startDateIsBetween) {
          const eventOldTotalMinutes = event.get('totalMinutes');
          const startDate = shiftDateIfOnNonWorkingDay(
            endDateParser(allocationEvent.endDate).add(1, 'day').startOf('day'),
            workingDaysConfig,
          );
          const eventTotalMinutes =
            numberOfWorkingDaysBetweenDates(
              startDate,
              moment(event.endDate),
              workingDaysConfig,
            ) * event.get('minutes');

          event.setStartDate(startDate.toDate(), false);
          event.set('totalMinutes', eventTotalMinutes);
          differenceInTotalMinutes =
            differenceInTotalMinutes - eventOldTotalMinutes + eventTotalMinutes;
          updateAllocation({
            ...asAllocationPayload(event),
            startDate: startDate.format(DEFAULT_DATE_FORMAT),
          });
          updatedToRange.toDate = (
            updatedToRange.toDate.isAfter(moment(event.endDate))
              ? updatedToRange.toDate
              : moment(event.endDate)
          ).endOf('day');
        } else if (endDateIsBetween) {
          const eventOldTotalMinutes = event.get('totalMinutes');
          const endDate = shiftDateIfOnNonWorkingDay(
            moment(allocationEvent.startDate).subtract(1, 'day').endOf('day'),
            workingDaysConfig,
            false,
          );
          const eventTotalMinutes =
            numberOfWorkingDaysBetweenDates(
              moment(event.startDate),
              endDate,
              workingDaysConfig,
            ) * event.get('minutes');

          event.setEndDate(endDate.toDate(), false);
          event.set('totalMinutes', eventTotalMinutes);
          differenceInTotalMinutes =
            differenceInTotalMinutes - eventOldTotalMinutes + eventTotalMinutes;
          updateAllocation({
            ...asAllocationPayload(event),
            endDate: endDate.format(DEFAULT_DATE_FORMAT),
          });
          updatedFromRange.fromDate = (
            updatedFromRange.fromDate.isBefore(moment(event.startDate))
              ? updatedFromRange.fromDate
              : moment(event.startDate)
          ).startOf('day');
        } else if (isDraggedEventInBetween) {
          const eventOldTotalMinutes = event.get('totalMinutes');
          const eventOldEndDate = event.endDate;
          const endDate = shiftDateIfOnNonWorkingDay(
            moment(allocationEvent.startDate).subtract(1, 'day').endOf('day'),
            workingDaysConfig,
            false,
          );
          const eventTotalMinutes =
            numberOfWorkingDaysBetweenDates(
              moment(event.startDate),
              endDate,
              workingDaysConfig,
            ) * event.get('minutes');

          event.setEndDate(endDate.toDate(), false);
          event.set('totalMinutes', eventTotalMinutes);
          differenceInTotalMinutes =
            differenceInTotalMinutes - eventOldTotalMinutes + eventTotalMinutes;
          updateAllocation({
            ...asAllocationPayload(event),
            endDate: endDate.format(DEFAULT_DATE_FORMAT),
          });

          // add new allocation
          const startDate = shiftDateIfOnNonWorkingDay(
            moment(allocationEvent.endDate).add(1, 'day').startOf('day'),
            workingDaysConfig,
          );
          const newAllocationParams: NewAllocationParam = {
            id: idForNewAllocation(),
            ...(event.get('userId') != null
              ? {
                  type: ResourceType.User,
                  userId: event.get('userId'),
                  roleId: event.get('roleId'),
                  referenceTeamId: event.get('referenceTeamId'),
                  overAllocations: event.get('overAllocations'),
                }
              : {
                  teamId: event.get('teamId'),
                  roleId: event.get('roleId'),
                  type: ResourceType.Team,
                }),
            minutes: event.get('minutes'),
            billable: event.get('billable'),
            totalMinutes:
              numberOfWorkingDaysBetweenDates(
                startDate,
                moment(eventOldEndDate),
                workingDaysConfig,
              ) * event.get('minutes'),
            projectPhase: event.get('projectPhase'),
            startDate: startDate.format(DEFAULT_DATE_FORMAT),
            endDate: moment(eventOldEndDate).format(DEFAULT_DATE_FORMAT),
            projectId: event.get('projectId'),
            medium: event.get('medium'),
          };
          const newAllocation = newAllocationEvent(
            groupBy,
            newAllocationParams,
            {
              excludeNonWorkingDays,
              workingDaysConfig,
            },
            resourceManagementConfig?.softAllocations.enabled,
          );
          const [added] =
            schedulerRef.current.instance.eventStore.add(newAllocation);

          createAllocation(asAllocationPayload(added), added.id);

          differenceInTotalMinutes += newAllocationParams.totalMinutes; // updating diff to compensate for added event
        }

        if (moment(event.startDate).isBefore(updatedFromRange.fromDate)) {
          updatedFromRange.fromDate = moment(event.startDate).startOf('day');
        }
        if (moment(event.endDate).isAfter(updatedToRange.toDate)) {
          updatedToRange.toDate = moment(event.endDate).endOf('day');
        }
      });

      const minFromDate = getMinimumStartDateFromListOfRanges([
        updatedFromRange,
        updatedToRange,
      ]);
      const maxToDate = getMaximumEndDateFromListOfRanges([
        updatedFromRange,
        updatedToRange,
      ]);

      bulkUpdate(actionsMap)
        .then(({ processSummaryUpdate, processTotalAllocationUpdate }) => {
          if (processSummaryUpdate) {
            updateAllocationAndSummary(
              userId != null
                ? {
                    [FetchableResourceType.UserList]: [
                      allocationEvent.get('userId'),
                    ],
                  }
                : {
                    [FetchableResourceType.RoleList]: [
                      allocationEvent.get('roleId'),
                    ],
                  },
              { fromDate: minFromDate, toDate: maxToDate },
            );
          }
          if (processTotalAllocationUpdate) {
            updateTotalAllocation(
              allocationEvent.get('projectId'),
              userId != null
                ? {
                    type: ResourceType.User,
                    userId,
                    roleId: allocationEvent.get('roleId'),
                    referenceTeamId: allocationEvent.get('referenceTeamId'),
                  }
                : {
                    teamId: allocationEvent.get('teamId'),
                    type: ResourceType.Team,
                  },
              differenceInTotalMinutes,
            );
          }
        })
        .finally(() => {
          schedulerRef.current.instance.resumeRefresh(true);
        });
    },
    [
      schedulerRef,
      workingDaysConfig,
      bulkUpdate,
      updateTotalAllocation,
      groupBy,
      excludeNonWorkingDays,
      resourceManagementConfig?.softAllocations.enabled,
      updateAllocationAndSummary,
      isHoursPerWeek,
    ],
  );
};
